import React from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CallMergeIcon from "@mui/icons-material/CallMerge";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import classes from "./DialogueRowActions.module.css";

/**
 * Compact icon buttons under a dialogue row's timings: add an empty dialogue
 * below it, merge it into the one above, or delete it. Merge and delete only
 * ask for confirmation; the screen does the work in onConfirm of
 * DialogueRowActionDialog.
 */
export const DialogueRowActions = ({
  canAdd = true,
  onAdd,
  canMerge,
  mergeDisabledReason,
  disabled,
  onMerge,
  onDelete,
}) => (
  <div className={classes.Actions}>
    <Tooltip
      title={
        canAdd ? "Add dialogue below" : "Clear the filters to add dialogues"
      }
      arrow
    >
      <span>
        <IconButton
          size="small"
          className={`${classes.ActionButton} ${classes.AddButton}`}
          onClick={onAdd}
          disabled={disabled || !canAdd}
          aria-label="Add dialogue below"
        >
          <AddIcon fontSize="inherit" />
        </IconButton>
      </span>
    </Tooltip>
    <Tooltip
      title={canMerge ? "Merge with dialogue above" : mergeDisabledReason}
      arrow
    >
      <span>
        <IconButton
          size="small"
          className={classes.ActionButton}
          onClick={onMerge}
          disabled={disabled || !canMerge}
          aria-label="Merge with dialogue above"
        >
          <CallMergeIcon fontSize="inherit" />
        </IconButton>
      </span>
    </Tooltip>
    <Tooltip title="Delete dialogue" arrow>
      <span>
        <IconButton
          size="small"
          className={`${classes.ActionButton} ${classes.DeleteButton}`}
          onClick={onDelete}
          disabled={disabled}
          aria-label="Delete dialogue"
        >
          <DeleteOutlineIcon fontSize="inherit" />
        </IconButton>
      </span>
    </Tooltip>
  </div>
);

const DialoguePreview = ({ label, dialogue, formatTime, getSpeakerName, accent }) => (
  <div className={`${classes.Preview} ${accent ? classes.PreviewAccent : ""}`}>
    <div className={classes.PreviewMeta}>
      <span className={classes.PreviewLabel}>{label}</span>
      <span>
        {getSpeakerName(dialogue.speaker)} · {formatTime(dialogue.start || 0)} –{" "}
        {formatTime(dialogue.end || 0)}
      </span>
    </div>
    <div className={classes.PreviewText}>
      {dialogue.text?.trim() || <em className={classes.PreviewEmpty}>No text</em>}
    </div>
  </div>
);

/**
 * Confirms a merge or delete. `action` is { type: "merge" | "delete",
 * dialogue, aboveDialogue?, merged? } or null when closed.
 */
export const DialogueRowActionDialog = ({
  action,
  busy,
  onCancel,
  onConfirm,
  formatTime,
  getSpeakerName,
}) => {
  const isMerge = action?.type === "merge";
  const speakersDiffer =
    isMerge && action.aboveDialogue?.speaker !== action.dialogue?.speaker;

  return (
    <Dialog
      open={Boolean(action)}
      onClose={busy ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: classes.DialogPaper }}
    >
      {action && (
        <>
          <DialogTitle className={classes.DialogTitle}>
            {isMerge ? "Merge with dialogue above?" : "Delete this dialogue?"}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={1.5}>
              {isMerge ? (
                <>
                  <DialoguePreview
                    label="Above"
                    dialogue={action.aboveDialogue}
                    formatTime={formatTime}
                    getSpeakerName={getSpeakerName}
                  />
                  <DialoguePreview
                    label="This"
                    dialogue={action.dialogue}
                    formatTime={formatTime}
                    getSpeakerName={getSpeakerName}
                  />
                  <DialoguePreview
                    label="Result"
                    dialogue={action.merged}
                    formatTime={formatTime}
                    getSpeakerName={getSpeakerName}
                    accent
                  />
                  {speakersDiffer && (
                    <Alert severity="warning">
                      The two dialogues have different characters. The merged
                      dialogue keeps{" "}
                      <strong>{getSpeakerName(action.aboveDialogue.speaker)}</strong>.
                    </Alert>
                  )}
                  <Alert severity="info">
                    Original and translated text are joined. The generated audio
                    of the dialogue above is kept, so regenerate it to cover the
                    new text.
                  </Alert>
                </>
              ) : (
                <>
                  <DialoguePreview
                    label="Dialogue"
                    dialogue={action.dialogue}
                    formatTime={formatTime}
                    getSpeakerName={getSpeakerName}
                  />
                  <Alert severity="error">
                    The dialogue, its translations and generated audio are
                    removed. This cannot be undone.
                  </Alert>
                </>
              )}
            </Stack>
          </DialogContent>
          <DialogActions className={classes.DialogActions}>
            <Button onClick={onCancel} disabled={busy} color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              color={isMerge ? "primary" : "error"}
              onClick={onConfirm}
              disabled={busy}
              disableElevation
              startIcon={
                busy ? (
                  <CircularProgress size={16} color="inherit" />
                ) : isMerge ? (
                  <CallMergeIcon />
                ) : (
                  <DeleteOutlineIcon />
                )
              }
            >
              {isMerge ? "Merge" : "Delete"}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
